import React, { useRef, useState } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useFragment, graphql } from 'react-relay';
import { Box, Text, xcss } from '@atlaskit/primitives';
import type { url_issueFieldUrlReadviewFull_UrlReadView$key as UrlFragment } from '@atlassian/jira-relay/src/__generated__/url_issueFieldUrlReadviewFull_UrlReadView.graphql';
import { EmptyFieldText } from '@atlassian/jira-issue-empty-field-text/src/ui/index.tsx';
import isValidEmailAddress from '@atlassian/jira-common-is-valid-email-address/src/index.tsx';
import prefixUrl from '@atlassian/jira-common-util-prefix-url/src/index.tsx';
import { SimpleInlineSmartLink } from '@atlassian/jira-issue-field-smart-link/src/ui/simple-inline-smart-link/index.tsx';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import messages from '@atlassian/jira-issue-empty-field-text/src/ui/messages.tsx';
import { useBespokeEmptyFieldTextGA } from '@atlassian/jira-issue-experiments/src/bespoke-empty-field-text-ga/index.tsx';
import { formatReadValue } from './utils.tsx';
import type { UrlReadViewProps } from './types.tsx';

// eslint-disable-next-line no-script-url
const forbiddenScheme = 'javascript:';

export const UrlReadView = ({
	fragmentRef,
	isSimpleInlineSmartLinkEnabled,
	shouldRenderHoverPopover,
}: UrlReadViewProps): React.JSX.Element => {
	const data = useFragment<UrlFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment url_issueFieldUrlReadviewFull_UrlReadView on JiraUrlField {
				id
				uri
			}
		`,
		fragmentRef,
	);

	const { uri: value } = data;
	const [isUrlErrorState, setIsUrlErrorState] = useState(false);
	const contentRef = useRef(null);

	const hoverPopoverRefFactory = shouldRenderHoverPopover
		? // eslint-disable-next-line react-hooks/react-compiler, react-hooks/rules-of-hooks
			useHoverPopoverContentRef(data.id)
		: null;

	let hoverPopoverRef: HoverPopoverContentRef = null;
	if (hoverPopoverRefFactory) {
		hoverPopoverRef = hoverPopoverRefFactory({
			content: value,
		});
	}

	const handleSmartLinkError = () => {
		setIsUrlErrorState(true);
	};

	const isBespokeEmptyFieldTextGA = useBespokeEmptyFieldTextGA();
	if (value == null || value === '') {
		return (
			<EmptyFieldText
				{...(isBespokeEmptyFieldTextGA && {
					placeholderText: messages.bespokenUrlPlaceholderEmptyFieldText,
				})}
			/>
		);
	}

	if (isSimpleInlineSmartLinkEnabled) {
		if (value.startsWith(forbiddenScheme)) {
			if (shouldRenderHoverPopover) {
				return (
					<Text maxLines={1} ref={hoverPopoverRef}>
						{value}
					</Text>
				);
			}

			return (
				<Box xcss={truncatedStyles} ref={contentRef}>
					{value}
				</Box>
			);
		}
		let renderContent = (ref: React.Ref<HTMLAnchorElement> | undefined) => (
			<SimpleInlineSmartLink
				anchorTarget="_blank"
				url={prefixUrl(value, '//')}
				anchorRef={ref}
				onError={shouldRenderHoverPopover ? handleSmartLinkError : undefined}
			/>
		);
		if (isValidEmailAddress(value)) {
			renderContent = (ref: React.Ref<HTMLAnchorElement> | undefined) => (
				<SimpleInlineSmartLink
					anchorTarget="_blank"
					url={`mailto:${value}`}
					text={value}
					anchorRef={ref}
					onError={shouldRenderHoverPopover ? handleSmartLinkError : undefined}
				/>
			);
		}

		if (shouldRenderHoverPopover && isUrlErrorState) {
			return renderContent(hoverPopoverRef);
		}

		return renderContent(undefined);
	}

	const lDisplayValue = formatReadValue(value);

	return <Ellipsis title={value}>{lDisplayValue}</Ellipsis>;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Ellipsis = styled.div({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});

const truncatedStyles = xcss({
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
});
